/**
 * PrivateBetaGate — wraps public marketing routes when PRIVATE_MODE is on.
 *
 * - Anonymous visitors → see the launch-soon splash with login + invite-code paths.
 * - Logged-in users (any role) → pass through to the wrapped page.
 * - When mode hasn't loaded yet → render nothing for ~200ms, then children
 *   (avoids splash flicker for returning members).
 * - The splash itself does NOT block /login, /register, /admin, or /privacy/terms;
 *   App.js only wraps the marketing-style public routes with this gate.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Lock, KeyRound, Mail, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

let _cachedMode = null;

export function PrivateBetaGate({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [mode, setMode] = useState(_cachedMode);
  const [loaded, setLoaded] = useState(_cachedMode !== null);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (_cachedMode !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/system/mode`);
        _cachedMode = data;
        if (!cancelled) {
          setMode(data);
          setLoaded(true);
        }
      } catch {
        _cachedMode = { private: false };
        if (!cancelled) {
          setMode({ private: false });
          setLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Mode unknown → render briefly nothing to avoid splash flicker for members.
  if (!loaded || authLoading) {
    return <div data-testid="private-gate-loading" className="min-h-dvh no-overflow-x bg-[#050505]" />;
  }

  // Not in private mode → pass through.
  if (!mode?.private) return children;

  // Authenticated visitor (any role) → pass through.
  if (user) return children;

  // Anonymous + private mode → splash.
  const trimmed = code.trim().toUpperCase();
  return (
    <div data-testid="private-beta-splash" className="min-h-dvh no-overflow-x bg-[#050505] text-white relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 0%, rgba(212,175,55,0.18) 0%, transparent 60%), radial-gradient(40% 40% at 80% 100%, rgba(212,175,55,0.08) 0%, transparent 60%)",
        }}
      />
      <div className="relative max-w-[640px] mx-auto px-6 pt-20 md:pt-28 pb-16">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/35 bg-[#D4AF37]/[0.07] px-3 py-1 text-[10.5px] uppercase tracking-[0.24em] text-[#F3E5AB] font-semibold mb-6">
          <Lock className="w-3 h-3" /> Private Beta
        </div>

        <h1
          data-testid="private-splash-title"
          className="font-display font-semibold tracking-tight leading-[1.05]"
          style={{ fontSize: "clamp(2.2rem, 5.4vw, 3.8rem)" }}
        >
          The Guild is opening.<br />
          <span className="text-gold-gradient">By invitation, for now.</span>
        </h1>

        <p data-testid="private-splash-message" className="mt-5 text-[15px] md:text-base text-zinc-300 leading-relaxed max-w-[520px]">
          {mode.message ||
            "National Driving Guild is in private beta while we finish onboarding our first cohort of instructors. Public sign-ups will reopen at launch."}
        </p>

        <div className="mt-9 grid gap-3">
          <Link
            to="/login"
            data-testid="private-splash-login-link"
            className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] px-4 py-3.5 transition group"
          >
            <span className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex-shrink-0">
              <Mail className="w-4 h-4 text-[#F3E5AB]" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-bold">Member sign in</span>
              <span className="block text-[12px] text-zinc-400">Already part of the cohort? Sign in to your account.</span>
            </span>
            <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-white" />
          </Link>

          <div data-testid="private-splash-invite-block" className="rounded-xl border border-[#D4AF37]/25 bg-[#0d0d0d] px-4 py-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex-shrink-0">
                <KeyRound className="w-4 h-4 text-[#F3E5AB]" />
              </span>
              <div>
                <p className="text-sm font-bold">Have an invite code?</p>
                <p className="text-[12px] text-zinc-400">Codes look like <code className="font-mono text-[#F3E5AB]">BETA-XXXXXXXX</code></p>
              </div>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!trimmed) return;
                window.location.href = `/register?beta_code=${encodeURIComponent(trimmed)}`;
              }}
              className="flex gap-2"
            >
              <input
                data-testid="private-splash-invite-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="BETA-XXXXXXXX"
                autoCapitalize="characters"
                spellCheck={false}
                className="flex-1 h-11 rounded-md bg-[#050505] border border-white/10 focus:border-[#D4AF37]/60 px-3 text-sm font-mono text-white outline-none transition"
              />
              <button
                data-testid="private-splash-invite-submit"
                type="submit"
                disabled={!trimmed}
                className="h-11 px-4 rounded-md bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] disabled:opacity-40 disabled:cursor-not-allowed text-[#050505] text-sm font-bold transition"
              >
                Continue
              </button>
            </form>
          </div>
        </div>

        <p className="mt-10 text-[12px] text-zinc-500">
          Want an invite when we open? Email{" "}
          <a className="text-[#F3E5AB] underline underline-offset-4" href="mailto:hello@nationaldrivingguild.co.uk">
            hello@nationaldrivingguild.co.uk
          </a>
          .
        </p>
      </div>
    </div>
  );
}

export default PrivateBetaGate;
